import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Table, Badge } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { GlobalContext } from '../../store/Provider';
import { fetchUpcomingTransports } from '../../services/TransportService';
import './upcoming-transports.css';

/**
 * Widget component to display the upcoming transports of the active supplier
 * Shows transports in a table format with driver, vehicle, and delivery details
 * Positioned at the top of the dashboard above the Basisprijs varkens widget
 *
 * Data comes from the transports webservice, which is already scoped to the
 * active supplier and to pickups from today onwards, sorted soonest first.
 */
const UpcomingTransports = () => {
  const { t } = useTranslation();
  const { suppliersState } = useContext(GlobalContext);

  const [transports, setTransports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const activeSupplierID = suppliersState.suppliers.activeID;

  // The webservice only serves the transports of the logged in user, so the uid
  // has to be the one we got back from the login call.
  const uid = useMemo(() => {
    try {
      const user_details = JSON.parse(localStorage.getItem('user_data'));
      return user_details ? user_details.uid : null;
    } catch (e) {
      return null;
    }
  }, []);

  // Refetch whenever the client is switched in the topbar, the active supplier
  // decides which transports the webservice returns.
  useEffect(() => {
    let cancelled = false;

    const loadTransports = async () => {
      if (!uid) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setFailed(false);

      try {
        const data = await fetchUpcomingTransports(uid);
        if (!cancelled) {
          setTransports(data);
        }
      } catch (err) {
        if (!cancelled) {
          setTransports([]);
          setFailed(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTransports();

    return () => {
      cancelled = true;
    };
  }, [uid, activeSupplierID]);

  const parseTransportDate = (dateString) => {
    if (!dateString) {
      return new Date(NaN);
    }

    // ISO format (2026-04-01T06:30:00)
    if (dateString.includes('T')) {
      return new Date(dateString);
    }

    const parts = dateString.split(' ');
    const timePart = parts[1] || '00:00:00';

    // Database datetime format (2026-04-01 06:30:00)
    if (parts[0].includes('-')) {
      return new Date(`${parts[0]}T${timePart}`);
    }

    // Convert DD/MM/YYYY HH:MM:SS to Date object
    const dateParts = parts[0].split('/');

    // dateParts[0] = day, dateParts[1] = month, dateParts[2] = year
    return new Date(`${dateParts[2]}-${dateParts[1]}-${dateParts[0]}T${timePart}`);
  };

  // The webservice sorts already, we keep the sort as a safety net so the
  // widget stays correct if that ever changes.
  const sortedTransports = useMemo(() => {
    return [...transports].sort((a, b) => {
      try {
        const dateA = parseTransportDate(a.deabddt);
        const dateB = parseTransportDate(b.deabddt);
        return dateA - dateB;
      } catch (e) {
        return 0;
      }
    });
  }, [transports]);

  const formatDateOnly = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = parseTransportDate(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      // Format as DD/MM/YYYY
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = parseTransportDate(dateString);
      if (isNaN(date.getTime())) {
        return '-';
      }
      // Format as HH:MM
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (e) {
      return '-';
    }
  };

  // A leg can be planned without a pig count, but a count of 0 is a real value.
  const formatPigs = (numberOfPigs) => {
    return numberOfPigs === null || numberOfPigs === undefined
      ? '-'
      : numberOfPigs;
  };

  const header = (
    <div className='upcoming-transports-header'>
      <h5 className={sortedTransports.length > 0 ? 'mb-4' : 'mb-0'}>
        <i className='mdi mdi-truck'></i>&nbsp;
        {t('dashboard.upcomingTransports') || 'Upcoming Transports'}
        {sortedTransports.length > 0 && (
          <>
            &nbsp;<Badge color='danger'>{sortedTransports.length}</Badge>
          </>
        )}
      </h5>
    </div>
  );

  if (loading || failed || sortedTransports.length === 0) {
    let message = t('dashboard.noUpcomingTransports') || 'No upcoming transports scheduled';

    if (loading) {
      message = t('dashboard.loadingTransports') || 'Loading transports...';
    } else if (failed) {
      message = t('dashboard.transportsError') || 'Could not load the transports';
    }

    return (
      <Card className='upcoming-transports-card'>
        <CardBody className='pb-3'>
          {header}
          <div
            className={`alert ${failed ? 'alert-danger' : 'alert-info'} mt-3 mb-0`}
            role='alert'
          >
            {message}
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className='upcoming-transports-card'>
      <CardBody className='pb-3'>
        {header}

        <div className='table-responsive'>
          <Table hover className='mb-0'>
            <thead>
              <tr>
                <th>{t('offers.transport.number') || '#'}</th>
                <th>{t('offers.transport.departure') || 'Departure'}</th>
                <th>{t('offers.transport.driver') || 'Driver'}</th>
                <th>{t('loadingplaces.code') || 'Loading place'}</th>
                <th>{t('offers.transport.pigs') || 'Pigs'}</th>
              </tr>
            </thead>
            <tbody>
              {sortedTransports.slice(0, 10).map((transport) => (
                <tr key={`${transport.deabdnr}-${transport.devranr}`}>
                  <td className='text-center'>
                    <strong>{transport.devranr || '-'}</strong>
                  </td>
                  <td className='departure-cell'>
                    <div className='departure-date'>{formatDateOnly(transport.deabddt)}</div>
                    <div className='departure-time'>{formatTimeOnly(transport.deabddt)}</div>
                  </td>
                  <td>
                    <div className='transport-driver-info'>
                      <strong>{transport.dechfnm || '-'}</strong>
                      <small className='d-block text-muted'>
                        {transport.debednm || '-'}
                      </small>
                      {transport.degsmnr ? (
                        <a
                          href={`tel:${transport.degsmnr}`}
                          className='text-decoration-none d-block mt-1 transport-phone-link'
                        >
                          <i className='mdi mdi-phone'></i> {transport.degsmnr}
                        </a>
                      ) : (
                        <small className='d-block mt-1 text-muted'>
                          <i className='mdi mdi-phone'></i> -
                        </small>
                      )}
                    </div>
                  </td>
                  <td className='loading-place-cell'>
                    <strong>{transport.delplce || '-'}</strong>
                  </td>
                   <td className='text-center'>
                     <strong>{formatPigs(transport.deaanta)}</strong>
                   </td>
                 </tr>
               ))}
             </tbody>
           </Table>
        </div>

        {sortedTransports.length > 10 && (
          <div className='mt-3 text-muted small'>
            <i className='mdi mdi-information'></i>&nbsp;
            {t('dashboard.showingFirstTransports') || `Showing first 10 of ${sortedTransports.length} transports`}
          </div>
        )}
      </CardBody>
    </Card>
  );
};

UpcomingTransports.propTypes = {
  // Component fetches its own data, no props needed
};

export default UpcomingTransports;
